import { type Model, GQL, Utils } from "@binale-tech/shared";
import { ProductDetails, ProductGroups, type ProductKey, ProductKeys } from "@domain/models/Product";
import { encodeKey } from "@domain/api/firebase/firebase";

export class ProductAccessLevels {
    static readonly Read: GQL.IProductAccessLevel = GQL.IProductAccessLevel.Read;
    static readonly Write: GQL.IProductAccessLevel = GQL.IProductAccessLevel.Write;

    static all() {
        return [this.Read, this.Write];
    }
}

const DOCUMENT_WRITE_PRODUCTS: readonly ProductKey[] = [
    ProductKeys.DMS,
    ProductKeys.ER,
    ProductKeys.FE,
    ProductKeys.KB,
    ProductKeys.Bank,
    ProductKeys.Deb,
    ProductKeys.LA,
    ProductKeys.POS,
    ProductKeys.AVM,
    ProductKeys.Banks,
    ProductKeys.CashRegister,
    ProductKeys.Invoices,
];

export class ProductAccessUtils {
    private static accountingProductKeys: Set<ProductKey>;
    private static accountingProductKeyList: GQL.IProductKey[];
    private static paymentSourceProductKeyList: GQL.IProductKey[];

    static getAccountingKeys(): Set<ProductKey> {
        if (!this.accountingProductKeys) {
            this.accountingProductKeys = new Set(ProductDetails.getGroup(ProductGroups.Buch).map((d) => d.pk));
        }
        return this.accountingProductKeys;
    }
    /** Ordered list of all accounting (Buch group) product keys. */
    static getAccountingProductKeyList(): GQL.IProductKey[] {
        if (!this.accountingProductKeyList) {
            this.accountingProductKeyList = ProductDetails.getGroup(ProductGroups.Buch).map(
                (d) => d.pk as GQL.IProductKey,
            );
        }
        return this.accountingProductKeyList;
    }
    /** Subset of accounting products that can act as a payment / saldo source (ER, ErA, Deb). */
    static getPaymentSourceProductKeyList(): GQL.IProductKey[] {
        if (!this.paymentSourceProductKeyList) {
            this.paymentSourceProductKeyList = this.getAccountingProductKeyList().filter((pk) =>
                Utils.PaymentUtils.isProductPaymentSource(pk),
            );
        }
        return this.paymentSourceProductKeyList;
    }
    static hasCompanyAccountingProducts(companyData: Pick<GQL.ICompany, "products" | "accountingYears">) {
        const accountingProductKeys = this.getAccountingKeys();
        const hasAccountingProduct = companyData?.products?.some((p) => accountingProductKeys.has(p.productKey));
        return Boolean(hasAccountingProduct);
    }
    static hasCompanyAccountingAccess(companyData: Pick<GQL.ICompany, "products" | "accountingYears">) {
        if (!companyData) {
            return false;
        }
        const hasCompanyAccountingProducts = this.hasCompanyAccountingProducts(companyData);
        if (!hasCompanyAccountingProducts) {
            return false;
        }
        return companyData?.accountingYears?.length > 0;
    }
    static hasUserAccounting(companyData: GQL.ICompany, user: Model.UserInterface) {
        if (!this.hasCompanyAccountingAccess(companyData)) {
            return false;
        }
        const accountingProductKeys = this.getAccountingKeys();
        return [...accountingProductKeys].some((pk) => this.canRead(pk, companyData, user));
    }

    static hasCompanyInvoiceData(
        companyGQL: Pick<GQL.ICompany, "invoiceAddress" | "invoiceEmail" | "invoiceReceiver" | "invoiceTax">,
    ) {
        return (
            companyGQL.invoiceReceiver &&
            companyGQL.invoiceEmail &&
            Object.values(companyGQL.invoiceAddress || {}).filter(Boolean).length >= 5 &&
            Object.values(companyGQL.invoiceTax || {}).filter(Boolean).length > 1
        );
    }

    static canRead(pk: ProductKey, companyGQL: GQL.ICompany, user: Model.UserInterface) {
        if (pk === ProductKeys.ER_A) {
            pk = ProductKeys.ER;
        }
        return (
            this.preCheck(companyGQL, user) &&
            this.hasCompanyProductAccess(pk, companyGQL) &&
            Boolean(this.getUserProductAccess(pk, companyGQL, user))
        );
    }

    static canWrite(pk: ProductKey, companyGQL: GQL.ICompany, user: Model.UserInterface) {
        if (pk === ProductKeys.ER_A) {
            pk = ProductKeys.ER;
        }
        return (
            this.preCheck(companyGQL, user) &&
            this.hasCompanyProductAccess(pk, companyGQL) &&
            this.getUserProductAccess(pk, companyGQL, user) === ProductAccessLevels.Write
        );
    }

    static canWriteAny(pks: readonly ProductKey[], companyGQL: GQL.ICompany, user: Model.UserInterface) {
        return pks.some((pk) => this.canWrite(pk, companyGQL, user));
    }

    /** Mirrors the backend saldo transfer guard: write on every product the input touches, FE drafts included. */
    static getSaldoTransferAccess(
        input: GQL.ICompanyTransferSaldoInput | null | undefined,
        companyGQL: GQL.ICompany,
        user: Model.UserInterface,
    ) {
        const products = Utils.SaldoTransfer.saldoTransferProducts(input);
        const canSubmit =
            products.length === 0
                ? this.canWriteAny(this.getAccountingProductKeyList(), companyGQL, user)
                : products.every((pk) => this.canWrite(pk, companyGQL, user));
        const missingFe = products.includes(GQL.IProductKey.Fe) && !this.canWrite(ProductKeys.FE, companyGQL, user);
        return { canSubmit, missingFe };
    }

    /** Mirrors the backend DOCUMENT_WRITE_PRODUCTS (ER_A is folded into ER by canWrite). */
    static canWriteDocuments(companyGQL: GQL.ICompany, user: Model.UserInterface) {
        return this.canWriteAny(DOCUMENT_WRITE_PRODUCTS, companyGQL, user);
    }

    static isCompanyOwner(companyGQL: Pick<GQL.ICompany, "owner">, user: Model.UserInterface) {
        const uid = user?.fireUser?.uid;
        return Boolean(uid) && companyGQL?.owner?.id === uid;
    }

    static canReviewRecord(pk: ProductKey, companyGQL: GQL.ICompany, user: Model.UserInterface) {
        return this.canRead(pk, companyGQL, user) && this.hasUserRole(companyGQL, user, GQL.IUserRole.RecordReviewer);
    }
    static canReviewDMS(companyGQL: GQL.ICompany, user: Model.UserInterface) {
        return this.hasUserRole(companyGQL, user, GQL.IUserRole.DmsReviewer);
    }

    static canExportDatev(companyGQL: GQL.ICompany, user: Model.UserInterface) {
        return this.hasUserRole(companyGQL, user, GQL.IUserRole.DatevExporter);
    }

    static canExportElster(companyGQL: GQL.ICompany, user: Model.UserInterface) {
        return this.hasUserRole(companyGQL, user, GQL.IUserRole.ElsterExporter);
    }

    protected static preCheck(companyGQL: GQL.ICompany, user: Model.UserInterface) {
        return Boolean(companyGQL) && user.isUserDataLoaded;
    }

    protected static getUserProductAccess(
        pk: ProductKey,
        companyGQL: GQL.ICompany,
        user: Model.UserInterface,
    ): GQL.IProductAccessLevel {
        if (user.isAdmin && user.isAdminWrite) {
            return ProductAccessLevels.Write;
        }
        if (user.isAdmin) {
            return ProductAccessLevels.Read;
        }
        if (companyGQL?.isLocked) {
            return undefined;
        }
        if (pk === ProductKeys.AccountingCommon) {
            const accountingAccessLevels = ProductDetails.getGroup(ProductGroups.Buch)
                .map((d) => d.pk)
                .map((accountingPk) => this.getUserProductAccess(accountingPk, companyGQL, user));
            const levelsSet = new Set(accountingAccessLevels);
            if (levelsSet.has(ProductAccessLevels.Write)) {
                return ProductAccessLevels.Write;
            }
            if (levelsSet.has(ProductAccessLevels.Read)) {
                return ProductAccessLevels.Read;
            }
            return undefined;
        }
        return companyGQL?.members
            ?.find((v) => v.emailKey === encodeKey(user.fireUser.email))
            ?.products?.find((v) => v.productKey === pk)?.access;
    }

    static hasUserRole(companyGQL: GQL.ICompany, user: Model.UserInterface, role: GQL.IUserRole): boolean {
        if (user.isAdmin && user.isAdminWrite) {
            return true;
        }
        const roles = companyGQL?.members?.find((v) => v.emailKey === encodeKey(user.fireUser.email))?.roles || [];
        return roles.includes(role);
    }

    static hasCompanyProductAccess(pk: ProductKey, companyGQL: GQL.ICompany) {
        if (!companyGQL) {
            return false;
        }
        if (pk === ProductKeys.AccountingCommon) {
            return this.hasCompanyAccountingAccess(companyGQL);
        }
        return Boolean(companyGQL?.products?.find((v) => v.productKey === pk));
    }
}
